import React, { useEffect, useState, useRef, useCallback } from 'react';
import { MessageCircle, Send, X } from 'lucide-react';
import api from '../../api/axios';
import toast from 'react-hot-toast';

interface ChatSummary {
  id: string;
  status: string;
  customer_name: string;
  customer_email: string;
  last_message: string | null;
  unread_count: number;
  last_customer_message_at: string | null;
  created_at: string;
}

interface ChatMessage {
  id: string;
  sender_type: 'customer' | 'admin';
  message: string;
  created_at: string;
}

const SupportChatAdminPage: React.FC = () => {
  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedChat, setSelectedChat] = useState<ChatSummary | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchChats = useCallback(async () => {
    try {
      const res = await api.get('/api/admin/support/chats');
      if (res.data.success) setChats(res.data.data || []);
    } catch {
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChats();
    const interval = setInterval(fetchChats, 15000);
    return () => clearInterval(interval);
  }, [fetchChats]);

  const fetchMessages = useCallback(async (chatId: string) => {
    try {
      const res = await api.get(`/api/admin/support/chats/${chatId}/messages`);
      if (res.data.success) setMessages(res.data.data || []);
    } catch {}
  }, []);

  const handleSelectChat = (chat: ChatSummary) => {
    setSelectedChat(chat);
    fetchMessages(chat.id);
    fetchChats();
  };

  useEffect(() => {
    if (selectedChat) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => fetchMessages(selectedChat.id), 5000);
      return () => {
        if (pollRef.current) clearInterval(pollRef.current);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChat]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChat) return;
    try {
      setIsSending(true);
      await api.post(`/api/admin/support/chats/${selectedChat.id}/messages`, { message: newMessage.trim() });
      setNewMessage('');
      await fetchMessages(selectedChat.id);
      fetchChats();
    } catch {
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const handleCloseChat = async () => {
    if (!selectedChat) return;
    if (!window.confirm('Close this chat? The customer can start a new one anytime.')) return;
    try {
      await api.put(`/api/admin/support/chats/${selectedChat.id}/close`);
      toast.success('Chat closed');
      setSelectedChat(null);
      setMessages([]);
      fetchChats();
    } catch {
      toast.error('Failed to close chat');
    }
  };

  const totalUnread = chats.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs tracking-widest uppercase text-nude mb-1">Management</p>
        <h1 className="font-serif text-3xl text-primary">
          Support Chat
          {totalUnread > 0 && (
            <span className="text-nude font-sans text-sm ml-3">
              ({totalUnread} unread)
            </span>
          )}
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chat list */}
        <div className="lg:col-span-1 bg-white border border-light overflow-hidden">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3].map((i) => <div key={i} className="skeleton h-16 rounded" />)}
            </div>
          ) : chats.length === 0 ? (
            <div className="p-12 text-center">
              <MessageCircle size={32} className="text-nude mx-auto mb-3" />
              <p className="text-xs tracking-widest uppercase text-nude">
                No support chats yet
              </p>
            </div>
          ) : (
            <div className="divide-y divide-light max-h-[70vh] overflow-y-auto">
              {chats.map((chat) => (
                <button
                  key={chat.id}
                  onClick={() => handleSelectChat(chat)}
                  className={`w-full text-left p-4 hover:bg-light/50 transition-colors ${
                    selectedChat?.id === chat.id ? 'bg-light' : ''
                  } ${chat.unread_count > 0 ? 'border-l-2 border-primary' : ''}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className={`text-xs ${chat.unread_count > 0 ? 'font-semibold text-primary' : 'text-secondary'}`}>
                      {chat.customer_name}
                    </p>
                    {chat.unread_count > 0 && (
                      <span className="bg-primary text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0">
                        {chat.unread_count}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-nude truncate">{chat.last_message || 'No messages yet'}</p>
                  <p className="text-xs text-nude mt-1">
                    {chat.status === 'closed' ? 'Closed' : new Date(chat.last_customer_message_at || chat.created_at).toLocaleString()}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Conversation */}
        <div className="lg:col-span-2 bg-white border border-light flex flex-col" style={{ minHeight: '500px' }}>
          {selectedChat ? (
            <>
              <div className="flex items-center justify-between border-b border-light px-6 py-4">
                <div>
                  <p className="text-sm font-medium text-primary">{selectedChat.customer_name}</p>
                  <p className="text-xs text-nude">{selectedChat.customer_email}</p>
                </div>
                {selectedChat.status !== 'closed' && (
                  <button
                    onClick={handleCloseChat}
                    className="flex items-center space-x-1 text-xs text-nude hover:text-red-500 transition-colors"
                  >
                    <X size={14} />
                    <span>Close Chat</span>
                  </button>
                )}
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-3">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] px-3 py-2 text-xs leading-relaxed ${
                        msg.sender_type === 'admin'
                          ? 'bg-primary text-white'
                          : 'bg-light text-primary'
                      }`}
                    >
                      {msg.message}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {selectedChat.status !== 'closed' && (
                <form onSubmit={handleSend} className="border-t border-light p-4 flex items-center space-x-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Reply to customer..."
                    className="flex-1 bg-light px-3 py-2 text-xs outline-none focus:ring-1 focus:ring-primary"
                  />
                  <button
                    type="submit"
                    disabled={isSending || !newMessage.trim()}
                    className="bg-primary text-white p-2.5 hover:bg-secondary transition-colors disabled:opacity-50"
                  >
                    <Send size={14} />
                  </button>
                </form>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center py-24">
              <MessageCircle size={32} className="text-nude mb-3" />
              <p className="text-xs tracking-widest uppercase text-nude">
                Select a chat to view
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportChatAdminPage;